/**
 * `/` — once the auth session is loaded, redirect to either
 * /<tenantSlug>/dashboard (signed-in with a tenant) or /auth/login
 * (signed-out). Reads the session via @ken/auth which the AuthProvider
 * has already populated by this point.
 */

import { useAuthStore } from "@ken/auth";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, type ReactElement } from "react";

export const Route = createFileRoute("/")({
  component: IndexRoute,
});

function IndexRoute(): ReactElement {
  const status = useAuthStore((s) => s.status);
  const tenantId = useAuthStore((s) => s.session?.tenantId);
  const navigate = useNavigate();

  useEffect(() => {
    // Wait for the AuthProvider's initial /session fetch to complete.
    // status starts as 'idle', flips to 'loading' on mount, then settles
    // into 'authenticated' or 'unauthenticated'. Only redirect after it
    // settles — otherwise we briefly see 'unauthenticated' before the
    // session loads and bounce a signed-in user to /auth/login.
    if (status === "idle" || status === "loading") return;

    if (status === "unauthenticated") {
      void navigate({ to: "/auth/login", replace: true });
    } else if (status === "authenticated" && tenantId) {
      window.location.replace(`/${tenantId}/dashboard`);
    }
  }, [status, tenantId, navigate]);

  return (
    <div className="grid place-items-center min-h-screen text-[var(--color-text-soft)]">
      <div className="text-center">
        <div className="font-[var(--font-hand)] text-[40px] text-[var(--color-text-strong)] mb-2">
          ken
        </div>
        <div className="text-sm">Loading…</div>
      </div>
    </div>
  );
}
